import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';
import { environment } from 'src/environments/environment';
import { OptimizerAuthService } from '../services/optimizer-auth.service';

export const OPTIMIZER_TOKEN_COOKIE = 'optimizer_token';

/**
 * Для запросов на сервис-оптимизатор (Optimization Engine, отдельный хост):
 *  - ставит Bearer JWT из cookie optimizer_token;
 *  - удаляет Authorization, проставленный глобальным main-интерсептором (иначе
 *    DRF-токен ядра улетал бы в чужой сервис и получал 401);
 *  - при 401 на не-login-запросе пробует продлить сессию (silentLogin через
 *    зашифрованные креды из sessionStorage) и повторяет запрос.
 *
 * Полностью повторяет паттерн AnalyticsAuthInterceptor. Сервис берём через
 * Injector лениво, чтобы не было кольца DI.
 */
@Injectable({ providedIn: 'root' })
export class OptimizerAuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly _cookieService: CookieService,
    private readonly _injector: Injector,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Страховка от вырожденного 'https://' (CI не подставил placeholder) — иначе
    // startsWith совпадёт со всеми https-запросами и сломает приложение.
    if (!environment.optimizerApiUrl.includes('.')) {
      return next.handle(req);
    }
    if (!req.url.startsWith(environment.optimizerApiUrl)) {
      return next.handle(req);
    }

    const authed = this._withAuth(req);
    return next.handle(authed).pipe(
      catchError((err: unknown) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          !req.url.includes('/auth/login')
        ) {
          const auth = this._injector.get(OptimizerAuthService);
          return from(auth.refreshFromPersisted()).pipe(
            switchMap((ok) => {
              if (!ok) return throwError(() => err);
              return next.handle(this._withAuth(req));
            }),
          );
        }
        return throwError(() => err);
      }),
    );
  }

  private _withAuth(req: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = req.headers.delete('Authorization');
    if (!req.url.includes('/auth/login')) {
      const token = this._cookieService.get(OPTIMIZER_TOKEN_COOKIE);
      if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return req.clone({ headers });
  }
}
